"use client";

import { useBranding } from "@/lib/tenant/context";
import Link from "next/link";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";

interface HeaderProps {
  /**
   * When true, shows only the Plantista logo without company name.
   * Use this before a retailer is selected (steps 1-3).
   */
  minimal?: boolean;
}

export function Header({ minimal = false }: HeaderProps) {
  const branding = useBranding();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // In minimal mode, always use Plantista branding
  const showRetailerBranding = !minimal;

  const companyName = showRetailerBranding
    ? branding.companyName &&
      !branding.companyName.toLowerCase().includes("alex garden center")
      ? branding.companyName
      : "Plantista"
    : "Plantista";

  const logoSrc = showRetailerBranding
    ? branding.logo || "/logo512.png"
    : "/logo512.png"; // Always Plantista logo in minimal mode

  // In retailer mode, suppress brand render until mounted so we never flash
  // the Plantista fallback before the retailer's branding is available
  // (cache or fetch). In minimal/consumer mode, Plantista is correct → render
  // immediately. SSR and first client paint are identical → no hydration mismatch.
  const brandVisible = !showRetailerBranding || mounted;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        isScrolled
          ? "bg-plantista-50/90 dark:bg-plantista-950/90 backdrop-blur-md border-plantista-200 dark:border-plantista-800 py-4"
          : "bg-transparent border-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link href="/" className="flex items-center gap-2 group cursor-pointer">
          {/* Square Background with Animation */}
          <div className="w-8 h-8 bg-plantista-100 dark:bg-white flex items-center justify-center rounded-sm group-hover:rotate-90 transition-transform duration-500 overflow-hidden p-1">
            {brandVisible ? (
              <img
                src={logoSrc}
                className="w-full h-full object-contain"
                alt={companyName || "Plantista Logo"}
              />
            ) : null}
          </div>

          {/* Brand Name — invisible placeholder reserves layout to avoid shift */}
          <span
            className="font-display font-bold text-xl tracking-tighter text-plantista-950 dark:text-white"
            style={brandVisible ? undefined : { visibility: "hidden" }}
            aria-hidden={brandVisible ? undefined : true}
          >
            {brandVisible ? companyName : "\u00A0"}
          </span>
        </Link>

        {/* Menu Items / Theme Toggle */}
        <div className="flex items-center gap-8">
          {mounted && (
            <button
              onClick={() =>
                setTheme(resolvedTheme === "dark" ? "light" : "dark")
              }
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-plantista-900 text-plantista-950 dark:text-plantista-50 transition-colors"
              aria-label="Toggle theme"
            >
              {resolvedTheme === "dark" ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
